import { useState, useEffect } from 'react'
import { reservationApi } from '../services/api'
import { Reservation } from '../types'
import { useAuth } from '../hooks/useAuth'

export default function MyPage() {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const { user, logout } = useAuth()

  useEffect(() => {
    fetchReservations()
  }, [])

  const fetchReservations = async () => {
    try {
      // 自分の予約一覧を取得（全件から自分のものをフィルタ）
      const response = await reservationApi.getAll({ limit: 50 })
      setReservations(response.data.reservations)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async (id: string) => {
    if (!window.confirm('この予約をキャンセルしますか？')) return
    try {
      await reservationApi.cancel(id)
      fetchReservations()
    } catch (err: any) {
      alert(err.response?.data?.error || 'キャンセルに失敗しました')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return { label: '確定', color: 'green' }
      case 'PENDING': return { label: '保留中', color: 'orange' }
      case 'CANCELLED': return { label: 'キャンセル済', color: 'red' }
      case 'COMPLETED': return { label: '利用済', color: 'gray' }
      default: return { label: status, color: 'black' }
    }
  }

  const canCancel = (reservation: Reservation) => {
    const startAt = new Date(reservation.startAt)
    const oneHourBefore = new Date(startAt.getTime() - 60 * 60 * 1000)
    return reservation.status === 'CONFIRMED' && new Date() < oneHourBefore
  }

  if (loading) return <div style={styles.loading}>読み込み中...</div>

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>マイページ</h1>
        <div style={styles.headerRight}>
          <span style={styles.userName}>{user?.name} さん</span>
          <button onClick={logout} style={styles.logoutButton}>ログアウト</button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>予約履歴</h2>
          <a href="/" style={styles.newReservationButton}>新しい予約をする</a>
        </div>

        {reservations.length === 0 ? (
          <div style={styles.empty}>
            <p>予約がありません</p>
            <a href="/">予約する</a>
          </div>
        ) : (
          <div style={styles.reservationList}>
            {reservations.map((r) => {
              const status = getStatusLabel(r.status)
              return (
                <div key={r.id} style={styles.reservationCard}>
                  <div style={styles.reservationHeader}>
                    <span style={{ ...styles.statusBadge, color: status.color, borderColor: status.color }}>
                      {status.label}
                    </span>
                    <span style={styles.reservationId}>ID: {r.id.slice(0, 8)}...</span>
                  </div>
                  <div style={styles.reservationBody}>
                    <p style={styles.reservationTime}>
                      <strong>開始:</strong> {formatDate(r.startAt)}
                    </p>
                    <p style={styles.reservationTime}>
                      <strong>終了:</strong> {formatDate(r.endAt)}
                    </p>
                    {r.pinCodes && r.pinCodes.length > 0 && r.status === 'CONFIRMED' && (
                      <div style={styles.pinBox}>
                        <p style={styles.pinLabel}>入室用PINコード</p>
                        <p style={styles.pin}>{r.pinCodes[0].pinCode}</p>
                      </div>
                    )}
                  </div>
                  {canCancel(r) && (
                    <button
                      onClick={() => handleCancel(r.id)}
                      style={styles.cancelButton}
                    >
                      キャンセルする
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '20px', maxWidth: '800px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  title: { color: '#1F4E79', margin: 0 },
  userName: { color: '#555', fontSize: '14px' },
  logoutButton: { padding: '8px 16px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  section: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle: { color: '#333', margin: 0 },
  newReservationButton: { padding: '8px 16px', backgroundColor: '#1F4E79', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '14px' },
  loading: { textAlign: 'center', padding: '40px' },
  empty: { textAlign: 'center', padding: '40px', color: '#888' },
  reservationList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  reservationCard: { border: '1px solid #eee', borderRadius: '8px', padding: '16px' },
  reservationHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  statusBadge: { padding: '4px 12px', border: '1px solid', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  reservationId: { color: '#aaa', fontSize: '12px' },
  reservationBody: { marginBottom: '12px' },
  reservationTime: { margin: '4px 0', fontSize: '14px', color: '#333' },
  pinBox: { backgroundColor: '#f0f7ff', border: '1px solid #1F4E79', borderRadius: '8px', padding: '12px', textAlign: 'center', marginTop: '8px' },
  pinLabel: { color: '#1F4E79', fontWeight: 'bold', fontSize: '12px', margin: '0 0 4px 0' },
  pin: { fontSize: '24px', fontWeight: 'bold', letterSpacing: '6px', color: '#1F4E79', margin: 0 },
  cancelButton: { width: '100%', padding: '8px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }
}
